import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';

import { Subject, takeUntil } from 'rxjs';
import { VerticalTabListModel } from 'src/app/model';
import { DataService } from 'src/app/services/data.service';
import { ServicecallService } from 'src/app/services/servicecall.service';
import { DescriptiveAnalysisService } from './descriptive-analysis.service';

@Component({
  selector: 'app-descriptive-analysis',
  templateUrl: './descriptive-analysis.component.html',
  styleUrls: ['./descriptive-analysis.component.css']
})
export class DescriptiveAnalysisComponent implements OnInit, OnDestroy {
  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'F9') {
      // Perform your action here
      this._performAction();
    }
  }
  @Input() 
  set entityData(value: any){
    this._entityId = value?._id;
    this._category = value?.category;
  }
 
  descriptiveConfig: any;
  lineChartData;
  groupedBarChartData;
  pieChartData;
  barChartData;
  classType: string = 'RUL_BRAKE_PAD';
  verticalTabList: VerticalTabListModel[];

  private _isMock: boolean = false;
  private _loadStatus: any;
  private _unSubscribe = new Subject<void>();
  private _entityId: string;
  private _category: string;
  
  constructor(private _discriptiveService: DescriptiveAnalysisService, private _sharedService: ServicecallService, private _dataService: DataService) {
    _dataService.events.pipe(takeUntil(this._unSubscribe)).subscribe((res: any) => {
      if (res.name === 'siteChanged' || res.name === 'apiRefresh') {
        this._initailize();
      }
    });
  }

  ngOnInit(): void {
    const tenantGenericConfig = this._sharedService.getConfiguration('tenantGenericConfig')?this._sharedService.getConfiguration('tenantGenericConfig'):{};
    this.descriptiveConfig = tenantGenericConfig ? tenantGenericConfig?.descriptiveAnalysis : null;
    const descriptiveChartList = tenantGenericConfig?.profile?.find((item: any) => item?.category === this._category)?.descriptiveAnalysisChart;
    this.verticalTabList = descriptiveChartList ? descriptiveChartList : tenantGenericConfig?.descriptiveAnalysisChart ;
    this._initailize();
  }

  ngOnDestroy(): void {
    this._unSubscribe.next();
    this._unSubscribe.complete();
  }

  /**
   * Loaders status
   * @returns  
   */
  loaderStatus() {
    return Object.values(this._loadStatus)?.every((item: boolean) => item);
  }

  /**
   * Determines whether tab clicked
   * @param item 
   */
  onTabClick(item: VerticalTabListModel) {
    this.classType = item?.key ? item?.key : 'RUL_BRAKE_PAD';
    this._initailize();
  }

  /**
   * Performs action
   */
  private _performAction() {
    this._isMock = !this._isMock;
    this._initailize();
  }

  /**
   * Initailizes descriptive analysis component
   */
  private _initailize() {
    this._initializeLoadStatus();
    this._initializeResetPadWear();
    this._initializeResetBrakeEvent();
    this._initializeResetBrakeEventGroup();
    this._getPadWear();
    this._getBrakeEvent();
    this._getBrakeEventGroup();
  }

  /**
   * Initializes load status
   */
  private _initializeLoadStatus() {
    this._loadStatus = {
      padWear: false,
      brakeEvent: false,
      brakeEventGroup: false
    };
  }

  /**
   * Initializes reset pad wear
   */
  private _initializeResetPadWear() {
    this.lineChartData = { xAxisData: [], yAxisData: [], xAxisLabel: this.descriptiveConfig?.chart3?.xAxisLabel, yAxisLabel: this.descriptiveConfig?.chart3?.yAxisLabel, xNameLocation: null, yNameLocation: null, tooltipTrigger: null, xAxisType: null, yAxisType: null, symbol: null, color: null };
  }

  /**
   * Gets pad wear
   */
  private _getPadWear() {
    this._discriptiveService.getPadWear(this._entityId, this._isMock, this.classType).pipe(takeUntil(this._unSubscribe)).subscribe({
      next: (resp: any) => {
        this._loadStatus.padWear = true;
        if (resp?.data?.length && resp?.data?.some((elem: any) => { if (Object.keys(elem)?.length) return true; else return false; })) {
          this.lineChartData.xAxisData = resp?.data?.map((item: any) => item?.distance?.toPrecision(5));
          this.lineChartData.yAxisData = resp?.data?.map((item: any) => item?.wearRate?.toPrecision(5));
          this.lineChartData.xAxisLabel = this.descriptiveConfig?.chart3?.[this.classType === 'RUL_BRAKE_PAD' ? 'xAxisLabelBp' : 'xAxisLabelCp'];
          this.lineChartData.yAxisLabel = this.descriptiveConfig?.chart3?.[this.classType === 'RUL_BRAKE_PAD' ? 'yAxisLabelBp' : 'yAxisLabelCp'];
          this.lineChartData.color = this.descriptiveConfig?.chart3?.color;
          this.lineChartData = JSON.parse(JSON.stringify(this.lineChartData));
        } else {
          this._initializeResetPadWear();
        }
      }, error: () => {
        this._loadStatus.padWear = true;
        this._initializeResetPadWear();
      }
    });
  }

  /**
   * Initializes reset brake event
   */
  private _initializeResetBrakeEvent() {
    this.pieChartData = { pieData: [], pieRadius: '50%', color: [] };
    this.barChartData = { barData: [], barXaxisData: [], xAxisLabel: '', yAxisLabel: '', color: [], yaxisLabelShow: false, barWidth: null };
  }

  /**
   * Gets brake event
   */
  private _getBrakeEvent() {
    this._discriptiveService.getBrakeEvent(this._entityId, this._isMock, this.classType).pipe(takeUntil(this._unSubscribe)).subscribe({
      next: (resp: any) => {
        this._loadStatus.brakeEvent = true;
        if (Object.keys(resp?.data ?? {})?.length) {
          let originalDecelerationsObject = {};
          let originalDSpeedObject = {};
          Object.keys(this.descriptiveConfig?.deceleration ?? {})?.forEach((item: any) => originalDecelerationsObject[item] = resp?.data?.decelerations[item]);
          Object.keys(this.descriptiveConfig?.speed ?? {})?.forEach((item: any) => originalDSpeedObject[item] = resp?.data?.speed[item]);
          this.pieChartData.xAxisLabel = this.descriptiveConfig?.chart1?.[this.classType === 'RUL_BRAKE_PAD' ? 'xAxisLabelBp' : 'xAxisLabelCp'];
          this.pieChartData.pieRadius = this.descriptiveConfig?.chart1?.pieRadius;
          this.pieChartData.pieData = Object.entries(originalDecelerationsObject).map(([key, value]) => ({ name: this.descriptiveConfig?.deceleration?.[key]?.label, value: value }));
          this.pieChartData.color = Object.keys(originalDecelerationsObject ?? {}).map((elem: any) => this.descriptiveConfig?.deceleration?.[elem]?.color);
          this.barChartData.barData = Object.values(originalDSpeedObject ?? {});
          this.barChartData.barXaxisData = Object.keys(originalDSpeedObject ?? {}).map((elem: any) => this.descriptiveConfig?.speed?.[elem]?.label);
          this.barChartData.xAxisLabel = this.descriptiveConfig?.chart2?.[this.classType === 'RUL_BRAKE_PAD' ? 'xAxisLabelBp' : 'xAxisLabelCp'];
          this.barChartData.yAxisLabel = this.descriptiveConfig?.chart2?.[this.classType === 'RUL_BRAKE_PAD' ? 'yAxisLabelBp' : 'yAxisLabelCp'];
          this.barChartData.color[0] = this.descriptiveConfig?.chart2?.color;
          this.barChartData.barWidth = this.descriptiveConfig?.chart2?.barWidth;
          this.pieChartData = JSON.parse(JSON.stringify(this.pieChartData));
          this.barChartData = JSON.parse(JSON.stringify(this.barChartData));
        } else {
          this._initializeResetBrakeEvent();
        }
      }, error: () => {
        this._loadStatus.brakeEvent = true;
        this._initializeResetBrakeEvent()
      }
    });
  }

  /**
   * Initializes reset brake event group
   */
  private _initializeResetBrakeEventGroup() {
    this.groupedBarChartData = { groupedBarData: [], xAxisLabel: '', yAxisLabel: '', color: [], yaxisLabelShow: false, barWidth: null };
  }

  /**
   * Gets brake event group
   */
  private _getBrakeEventGroup() {
    this._discriptiveService.getBrakeEventGroup(this._entityId, this._isMock, this.classType).pipe(takeUntil(this._unSubscribe)).subscribe({
      next: (resp: any) => {
        this._loadStatus.brakeEventGroup = true;
        if (Object.keys(resp?.data ?? {})?.length) {
          this.groupedBarChartData.barWidth = this.descriptiveConfig?.chart4?.barWidth;
          this.groupedBarChartData.xAxisLabel = this.descriptiveConfig?.chart4?.[this.classType === 'RUL_BRAKE_PAD' ? 'xAxisLabelBp' : 'xAxisLabelCp'];
          this.groupedBarChartData.yAxisLabel = this.descriptiveConfig?.chart4?.[this.classType === 'RUL_BRAKE_PAD' ? 'yAxisLabelBp' : 'yAxisLabelCp'];
          let originalObject = {};
          Object.keys(this.descriptiveConfig?.deceleration ?? {})?.forEach((item: any) => originalObject[item] = resp?.data?.[item]);
          this.groupedBarChartData.color = Object.keys(this.descriptiveConfig?.speed ?? {})?.map((elem: string) => this.descriptiveConfig?.speed?.[elem]?.color);
          let legendData: any = Object.keys(this.descriptiveConfig?.speed ?? {})?.map((elem: string) => this.descriptiveConfig?.speed?.[elem]?.label);
          legendData.unshift('legend');
          this.groupedBarChartData?.groupedBarData.push(legendData);
          const subOriginalArray = Object.entries(originalObject).map(([key, value]) => ({ key, value }));
          subOriginalArray.forEach((elem: any) => {
            let transformedArry = [];
            transformedArry.push(this.descriptiveConfig?.deceleration?.[elem?.key]?.label);
            Object.keys(this.descriptiveConfig?.speed ?? {})?.forEach((item: any) => {
              transformedArry.push(elem.value?.[item]?.value)
            });
            this.groupedBarChartData?.groupedBarData.push(transformedArry);
          })
          this.groupedBarChartData = JSON.parse(JSON.stringify(this.groupedBarChartData));
        } else {
          this._initializeResetBrakeEventGroup();
        }
      }, error: () => {
        this._loadStatus.brakeEventGroup = true;
        this._initializeResetBrakeEventGroup();
      }
    });
  }

}
